"use client";

//region Imports
import React, {useEffect, useRef, useState, useMemo, useCallback} from "react";
import {useForm, FormProvider} from "react-hook-form";
import _ from "lodash";

import {$core, HttpStatus} from "@isudio/core";
import {$store} from '@isudio/globals';
import {pages, policies} from "@isudio/config";
import {getCountries, getCountryByCode, useMergeRefs} from "@isudio/utils";
import type {IUser, IBrief, IBriefPayload, BriefDomainKey} from "@isudio/data";
import type {FooterElement, HeaderElement, BaseDialogProps} from "../";
import {LoadingSpinner, Confirm, ToolbarTop, ToolbarBottom, InputValidator, PhoneInput, RowGrid, useDialogController, useTippyRegistry} from "../";
//endregion imports

//region Types & Interfaces
interface BriefProps extends BaseDialogProps {
  dialogOptions?: any;
  to?: string;
  onSuccess?: () => void;
  onClose?: () => void;
}

const DOMAIN_MAP: Record<string, BriefDomainKey> = {
  "Business Strategy Transformation": "businessStrategy",
  "Brand Identity Transformation": "brandIdentity",
  "Research Insights Transformation": "researchInsights",
  "Business Technology Transformation": "businessTechnology"
};
//endregion Types & Interfaces

export function Brief({
                        dialogOptions,
                        to = pages.brief.to,
                        onSuccess,
                        onClose
                      }: BriefProps) {
  //region Variables
  const [loading, setLoading] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isOverflow, setIsOverflow] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const showFooter = !isOverflow || isAtBottom;
  const {name} = dialogOptions;
  const className = name.toLowerCase().replace(/ /g, "-");
  const [resetKey, setResetKey] = useState(0);
  const [step, setStep] = useState(0);

  const nameRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const phoneRef = useRef<HTMLInputElement | null>(null);
  const organizationRef = useRef<HTMLInputElement | null>(null);
  const designationRef = useRef<HTMLInputElement | null>(null);
  const lastFocusedRef = useRef<any | null>(null);

  const user: IUser = _.cloneDeep($store.User?.user);
  const userPolicy = _.get($store, `Data.userPolicy$.value.fields`, policies.userPolicy.fields);
  const briefConfig = pages.brief;
  const transformCountry = (c: any) => {
    return {
      label: c.name.english,
      value: c.code.phone,
      code: c.code.short2.toLowerCase(),
      imageUrl: `/assets/fonts/flag-icons/flags/png/${c.code.short2.toLowerCase()}.png`
    };
  };

  const country = transformCountry(!_.isNothing(_.get(user, 'country.code', null)) ? getCountryByCode(user.country.code) : _.get($store.Device.data, "location.geoCode.country", $store.Data.countries$.getValue().default));
  const countries = getCountries().map((c: any) => {
    return {
      label: c.name,
      value: c.dialCode,
      code: c.code,
      imageUrl: c.imageUrl
    };
  });

  const userName = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
  const defaults: IBrief = useMemo(() => ({
    to: to,
    name: (userName || ""),
    email: user?.email || "",
    country: user?.country || undefined,
    phone: user?.mobile || "",
    organization: user?.organization || "",
    designation: user?.designation || "",
    selectedDomains: [],
    domains: {},
    readiness: ""
  }), [user, userName, to]);

  const contactForm = useForm<IBrief>({
    mode: "onChange",
    criteriaMode: "all",
    defaultValues: defaults
  });

  const domainsForm = useForm<any>({
    mode: "onChange",
    criteriaMode: "all",
    defaultValues: {}
  });

  const readinessForm = useForm<any>({
    mode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      readiness: ""
    }
  });

  const { formState: { errors }, trigger, register, getValues, setError, watch } = contactForm;
  const selectedDomains = contactForm.watch("selectedDomains") || [];
  const selectedDomainsKey = selectedDomains.join(",");

  // Watch all domain values continuously to drive Next button state layout transitions
  const watchedDomainValues = domainsForm.watch();
  const watchedReadinessValue = readinessForm.watch("readiness");

  const dynamicSteps = useMemo(() => {
    const sequence: { type: "domain" | "readiness"; key: BriefDomainKey | "readiness"; config: any }[] = [];
    selectedDomains.forEach(domainStr => {
      const internalKey = DOMAIN_MAP[domainStr];
      if (internalKey && briefConfig[internalKey]) {
        sequence.push({ type: "domain", key: internalKey, config: briefConfig[internalKey] });
      }
    });
    sequence.push({ type: "readiness", key: "readiness", config: briefConfig.readiness });
    return sequence;
  }, [selectedDomains, briefConfig]);

  const currentMeta = dynamicSteps[step - 1];
  const totalSteps = dynamicSteps.length + 1;

  const isNextDisabled = useMemo(() => {
    if (step === 0) {
      return !contactForm.formState.isValid || selectedDomains.length === 0;
    }

    if (!currentMeta) return false;

    if (currentMeta.type === "domain") {
      const domainKey = currentMeta.key as BriefDomainKey;
      const questionsList = currentMeta.config?.questions || [];

      if (questionsList.length === 0) return false;

      // FIX: Replaced custom layout _.get with native optional chaining navigation safe from production build errors
      return !questionsList.every((_: any, qIdx: number) => {
        const val = watchedDomainValues?.[domainKey]?.questions?.[`q_${qIdx}`];
        return typeof val === "string" && val.trim().length > 0;
      });
    }

    if (currentMeta.type === "readiness") {
      return !watchedReadinessValue || watchedReadinessValue.trim().length === 0;
    }

    return false;
  }, [
    step,
    selectedDomains,
    contactForm.formState.isValid,
    watchedDomainValues,
    watchedReadinessValue,
    currentMeta
  ]);

  const formStateRef = useRef({ isDirty: false });
  formStateRef.current = {
    isDirty: contactForm.formState.isDirty || domainsForm.formState.isDirty || readinessForm.formState.isDirty
  };
  //endregion Variables

  //region Form Validations
  const validations: any = {
    name: {
      required: "Name is required.",
      minLength: {
        value: userPolicy.name.minLength,
        message: `Name should have at least ${userPolicy.name.minLength} characters.`,
      },
      maxLength: {
        value: userPolicy.name.maxLength,
        message: `Name can't exceed ${userPolicy.name.maxLength} characters.`,
      },
      pattern: {
        value: /^[a-zA-Z ]*$/,
        message: "Invalid characters entered.",
      }
    },
    email: {
      required: "Email is required.",
      minLength: {
        value: userPolicy.email.minLength,
        message: `Email should have at least ${userPolicy.email.minLength} characters.`,
      },
      maxLength: {
        value: userPolicy.email.maxLength,
        message: `Email can't exceed ${userPolicy.email.maxLength} characters.`,
      },
      pattern: {
        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: "Please enter a valid email address.",
      },
      valueExists: {
        message: "Email “{value}” has already signed up.\nPlease sign in if it is yours or use a different email."
      }
    },
    phone: {
      minLength: {
        value: userPolicy.mobile.minLength,
        message: `Phone number should have at least ${userPolicy.mobile.minLength} numbers.`,
      },
      maxLength: {
        value: userPolicy.mobile.maxLength,
        message: `Phone number can't exceed ${userPolicy.mobile.maxLength} numbers.`,
      },
      pattern: {
        value: /^[0-9+ ]*$/,
        message: "Invalid characters entered.",
      }
    },
    organization: {
      maxLength: {
        value: userPolicy.organization.maxLength,
        message: `Organization name can't exceed ${userPolicy.organization.maxLength} characters.`,
      }
    },
    designation: {
      maxLength: {
        value: userPolicy.designation.maxLength,
        message: `Designation can't exceed ${userPolicy.designation.maxLength} characters.`,
      }
    }
  }
  //endregion Form Validations

  //region Form Registrations
  const {
    ref: nameRegisterRef,
    onBlur: nameRegisterOnBlur,
    ...nameRegister
  } = register("name", validations.name);

  const {
    ref: emailRegisterRef,
    onBlur: emailRegisterOnBlur,
    ...emailRegister
  } = register("email", _.omit(validations.email, "valueExists"));

  const {
    ref: phoneRegisterRef,
    onBlur: phoneRegisterOnBlur,
    ...phoneRegister
  } = register("phone", validations.phone);

  const {
    ref: organizationRegisterRef,
    onBlur: organizationRegisterOnBlur,
    ...organizationRegister
  } = register("organization", validations.organization);

  const {
    ref: designationRegisterRef,
    onBlur: designationRegisterOnBlur,
    ...designationRegister
  } = register("designation", validations.designation);
  //endregion Form Registrations

  //region Methods & Event Handlers
  const {
    openDialog,
    closeDialog,
    setOnBeforeClose,
    setOnClose
  } = useDialogController();

  const {
    suppress,
    unsuppress
  } = useTippyRegistry();

  const hasValue = (v?: string | null) => !!v?.trim();

  const onNext = async () => {
    if (step === 0) {
      const isContactValid = await trigger(["name", "email", "selectedDomains"]);
      if (!isContactValid) return;
      if (selectedDomains.length === 0) {
        _.toastError("Please select at least one transformation domain.");
        return;
      }
      setStep(1);
    } else {
      const currentStepMeta = dynamicSteps[step - 1];
      // This guard clause tells TypeScript that currentStepMeta is guaranteed to be defined below this point
      if (!currentStepMeta) return;

      // 1. Handle validation check for dynamic question pages
      if (currentStepMeta.type === "domain") {
        const domainKey = currentStepMeta.key as BriefDomainKey;
        const questionsList = currentStepMeta.config?.questions || [];

        // Use the precise value matching that coordinates perfectly with your Next button state
        const isCurrentStepComplete = questionsList.every((_: any, qIdx: number) => {
          const val = watchedDomainValues?.[domainKey]?.questions?.[`q_${qIdx}`];
          return typeof val === "string" && val.trim().length > 0;
        });

        if (!isCurrentStepComplete) {
          _.toastError(`Please answer all questions in ${currentStepMeta.config.heading}.`);
          return;
        }
      }
      // 2. Handle validation check for readiness page
      else if (currentStepMeta.type === "readiness") {
        if (!watchedReadinessValue || watchedReadinessValue.trim().length === 0) {
          _.toastError("Please declare your readiness status before submitting.");
          return;
        }
      }

      if (step < totalSteps - 1) {
        setStep(prev => prev + 1);
      } else {
        await _onSend();
      }
    }
    contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onBack = () => {
    if (step > 0) setStep(prev => prev - 1);
    contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setFirstFocus = () => {
    if (!user) return nameRef?.current?.focus();
    if (!user?.mobile) return phoneRef?.current?.focus();
    if (!user?.organization) return organizationRef?.current?.focus();
    if (!user?.designation) return designationRef?.current?.focus();
    return;
  }

  const onPhoneChange = async (value: any) => {
    const country = value.country;
    void contactForm.setValue("country", country, {shouldDirty: true});
    const phone = value.number;
    void contactForm.setValue("phone", phone, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const onPhoneBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value.length < 1) return;
    const dialCode = e.target.value.replace(/^\+(\d+).*/, "$1");
    const phone = e.target.value.replace(/^\+\d+\s*/, ``).replace(/\s/g, ``)
    if (dialCode === defaults.country?.dialCode && phone === defaults.phone) return;
    await phoneRegisterOnBlur(e);
    await trigger("phone");
  };

  const onReset = () => {
    setResetKey((prev) => prev + 1);
    contactForm.reset();
    domainsForm.reset();
    readinessForm.reset();
    setStep(0);
    const t = setTimeout(() => {
      setFirstFocus();
      clearTimeout(t);
    }, 10);
  };

  const _onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    await _onSend();
  };

  const _onSend = async () => {
    suppress();
    const contactValues = contactForm.getValues();
    const rawDomainsValues = domainsForm.getValues();
    const readinessValues = readinessForm.getValues();

    const formattedDomainsList: Array<{
      domain: string;
      response: Array<{ question: string; answer: string }>;
    }> = [];

    Object.keys(rawDomainsValues).forEach((domainKey) => {
      const answersObj = rawDomainsValues[domainKey]?.questions;
      const configMeta = briefConfig[domainKey as BriefDomainKey];

      if (answersObj && configMeta && Array.isArray(configMeta.questions)) {
        const domainAnswers: Array<{ question: string; answer: string }> = [];

        Object.keys(answersObj).forEach((qKey) => {
          const matchIndex = qKey.match(/\d+/);
          if (matchIndex) {
            const index = parseInt(matchIndex[0], 10);
            const explicitQuestionText = configMeta.questions[index]?.question;
            const chosenAnswerText = answersObj[qKey];

            if (explicitQuestionText && chosenAnswerText) {
              domainAnswers.push({
                question: explicitQuestionText,
                answer: chosenAnswerText
              });
            }
          }
        });

        if (domainAnswers.length > 0) {
          formattedDomainsList.push({
            domain: configMeta.heading || domainKey,
            response: domainAnswers
          });
        }
      }
    });

    const finalCollatedData: any = {
      ...contactValues,
      domains: formattedDomainsList,
      ...readinessValues
    };

    setLoading(true);
    const cleanValues = _.omitBy(finalCollatedData, (value) => _.isNil(value) || _.isNothing(value));
    const values = _.omitBy(_.omit(cleanValues, "selectedDomains"), (value) => _.isNil(value) || _.isNothing(value));
    const phone = values.phone ? `+${values.country?.dialCode} ${values?.phone ? values.phone.replace(/(\d{3})(?=\d{3})/g, '$1 ').trim() : ''}` : null;

    const payload: IBriefPayload = _.omitBy({
      ...values,
      phone,
      device: {
        ...($store.Device.data || null),
        ...{deviceTime: new Date().toISOString()}
      }
    }, _.isNil) as IBriefPayload;

    await $core.brief(payload).then(async () => {
      setLoading(false);
      await closeDialog(true);
      onSuccess?.();

    }).catch((e: any) => {
      setLoading(false);
      if (e?.code === HttpStatus.BAD_REQUEST && _.isArray(e?.data) && e?.data?.length > 0) {
        const fieldErrors = e?.data?.filter((f: any) => ["invalidData", "validationError"].indexOf(f?.error) < 0);
        fieldErrors.forEach((f: any) => {
          const fieldName = f?.attribute as keyof IBrief;
          const message = _.get(validations, `${fieldName}.${f?.error === "required" ? "required" : `${f?.error}.message`}`, null)
            .replace("{value}", getValues(fieldName));

          if (fieldName && message) {
            setError(fieldName, {
              type: "validate",
              message
            });
          }
        });
        _.toastError(`Send brief form contains invalid data.\nPlease correct highlighted fields and submit again.`);

      } else {
        _.toastError(_.get(e, 'message'));
      }
    });
  };

  const onScroll = () => {
    const el = contentRef.current;
    if (!el) return;

    const distanceFromTop = el.scrollTop;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);

    const SHOW_THRESHOLD = 5;
    const HIDE_THRESHOLD = 25;
    const TOP_HIDE_THRESHOLD = 40;

    setIsAtBottom(prev => {
      if (!prev && distanceFromBottom <= SHOW_THRESHOLD) return true;
      if (distanceFromTop <= TOP_HIDE_THRESHOLD) return false;
      if (prev && distanceFromBottom > HIDE_THRESHOLD) return false;
      return prev;
    });
  };

  const _onBeforeClose = useCallback(() => {
    if (!formStateRef.current.isDirty) return true;

    return new Promise<boolean>((resolve) => {
      suppress();

      openDialog(Confirm, {
        ...pages.confirm.properties,
        text: "Discard changes?",

        onYes: async () => {
          unsuppress();
          await closeDialog(true);
          resolve(true);
        },

        onNo: async () => {
          unsuppress();
          if (lastFocusedRef?.current) {
            if (lastFocusedRef.current === "phone") {
              phoneRef.current?.focus();
            } else {
              contactForm.setFocus(lastFocusedRef.current);
            }
          }
          resolve(false);
        }
      });
    });
  }, []);

  const _onClose = useCallback(() => {
    onClose?.();
  }, [onClose]);
  //endregion Event Handlers

  //region Effects
  useEffect(() => {
    if (!footerRef.current) return;

    const el = footerRef.current;
    const updateHeight = () => {
      setFooterHeight(el.scrollHeight);
    };

    updateHeight();

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;

    const el = contentRef.current;

    const checkOverflow = () => {
      requestAnimationFrame(() => {
        setIsOverflow(el.scrollHeight > el.clientHeight);
      });
    };

    checkOverflow();

    const observer = new ResizeObserver(() => checkOverflow());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;

    const rafId = requestAnimationFrame(() => {
      setIsOverflow(contentRef.current!.scrollHeight > contentRef.current!.clientHeight);
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [step, selectedDomainsKey]);

  useEffect(() => {
    setFirstFocus();
  }, []);

  useEffect(() => {
    setOnBeforeClose(_onBeforeClose);
  }, [setOnBeforeClose, _onBeforeClose]);

  useEffect(() => {
    setOnClose(_onClose);
  }, [setOnClose, _onClose]);
  //endregion Effects

  //region Toolbar Elements
  const headerElements: HeaderElement[] = [
    {
      name: "title",
      type: "span",
      position: "left",
      label: `${briefConfig.title} ${step > 0 ? `(${step}/${totalSteps - 1})` : ""}`,
      style: "text-sm sm:text-md md:text-lg font-bold text-white tracking-tight md:tracking-wider"
    },
    {
      name: "reset",
      type: "button",
      position: "right",
      icon: "fa-rotate",
      tooltip: "Reset",
      style: "text-white scale-90 transition duration-200 hover:text-generic-hover hover:scale-120",
      hidden: !contactForm.formState.isDirty,
      onClick: () => onReset()
    },
    {
      name: "close",
      type: "button",
      position: "right",
      icon: "fa-square-xmark",
      tooltip: "Close",
      style: "text-white scale-115 transition duration-200 hover:text-light-red hover:scale-125",
      onClick: () => closeDialog()
    }
  ];

  const footerElements: FooterElement[] = [
    {
      name: "back",
      type: "gradientButton",
      action: "button",
      label: "Back",
      style: `btn-cancel btn-gray-reverse ${step === 0 ? "hidden" : ""}`,
      onClick: onBack
    },
    {
      name: "next",
      type: "gradientButton",
      action: "button",
      label: step === totalSteps - 1 ? "Send Brief" : "Next",
      style: `btn-submit btn-${!isNextDisabled ? 'blue transition duration-200 hover:text-generic-hover hover:scale-105' : 'gray'}`,
      disabled: isNextDisabled,
      onClick: onNext
    }
  ];
  //endregion Toolbar Elements

  //region HTML Elements
  const loadingSpinner = (
    <LoadingSpinner
      className="absolute"
      borderRadius="rounded-sm md:rounded-md"
      label="Sending message..."
    />
  );

  const toolbarTop = (
    <ToolbarTop
      name={`tbt-${className}`}
      elements={headerElements}
      leftToolbarGap="gap-1"
    />
  );

  const toolbarBottom = (
    <ToolbarBottom
      ref={footerRef}
      name={`tbb-${className}`}
      elements={footerElements}
    />
  );

  const staticFooter = (
    <div>
      {toolbarBottom}
    </div>
  );

  const fixedFooter = (
    <div
      className={`
          absolute bottom-0 left-0 right-0 z-50
          transition-transform duration-300 ease-in-out
          ${showFooter ? "translate-y-0" : "translate-y-full"}
        `}
    >
      {toolbarBottom}
    </div>
  );

  const content = (
    <div
      ref={contentRef}
      onScroll={onScroll}
      className={`
          content-${className}
          flex-1 min-h-0 box-border overflow-y-auto 
          bg-[radial-gradient(circle,#ffffff_0%,#d6d6d6_100%)] 
          dark:bg-[radial-gradient(circle,#030b11b8_0%,#030b11d8_100%)] 
          scrollbar-track-rounded-full scrollbar-thin 
          scrollbar-track-dim-white dark:scrollbar-track-transparent 
          scrollbar-thumb-custom-blue-600 
          scrollbar-hover:scrollbar-thumb-custom-blue-300 
          scrollbar-active:scrollbar-thumb-custom-blue-300
          ${isOverflow ? "pt-4" : "py-4"}
        `}
      style={{
        paddingBottom: isOverflow ? footerHeight + 16 : undefined
      }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}>

      {/* STEP 0 Form Layout Wrapper */}
      <div style={{ display: step === 0 ? "block" : "none" }}>
        <FormProvider {...contactForm}>
          <div className="section-brief grid px-4 gap-4 text-sm md:text-md text-custom-gray-600 dark:text-pale-white leading-relaxed">
            <div className="relative w-full h-full flex flex-col justify-center gap-2">
              <div className="font-black text-md text-black dark:text-ghost-white tracking-normal">
                {briefConfig.heading}
              </div>
              <div className="max-w-104 text-xs md:text-sm italic text-custom-gray-600 dark:text-pale-white tracking-tight whitespace-pre-line">
                {briefConfig.briefChoices.text}
              </div>
            </div>

            <div
              className="flex flex-col justify-center items-center self-center0">
              <InputValidator
                ref={useMergeRefs(nameRef, nameRegisterRef)}
                placeholder="Name*"
                minLength={userPolicy.name.minLength}
                maxLength={userPolicy.name.maxLength}
                mask={/[a-zA-Z ]/}
                format="properCase"
                defaultValue={(hasValue(userName) ? userName : "")}
                tabIndex={(hasValue(userName) ? -1 : 0)}
                readOnly={hasValue(userName)}
                value={watch("name")}
                error={errors.name?.message}
                {...nameRegister}
                showToast={_.toastError}
                onFocus={() => (lastFocusedRef.current = nameRegister.name)}
                onBlur={async (e) => {
                  await nameRegisterOnBlur(e);
                  await trigger("name");
                }}
              />
            </div>

            <RowGrid>
              <InputValidator
                ref={useMergeRefs(emailRef, emailRegisterRef)}
                placeholder="Email*"
                minLength={userPolicy.email.minLength}
                maxLength={userPolicy.email.maxLength}
                mask={/[a-zA-Z0-9@._%+-]/}
                format="email"
                defaultValue={(hasValue(user?.email) ? user?.email : "")}
                tabIndex={(hasValue(user?.email) ? -1 : 0)}
                readOnly={(hasValue(user?.email))}
                value={watch("email")}
                error={errors.email?.message}
                {...emailRegister}
                showToast={_.toastError}
                onFocus={() => (lastFocusedRef.current = emailRegister.name)}
                onBlur={async (e) => {
                  await emailRegisterOnBlur(e);
                  await trigger("email");
                }}
              />

              <PhoneInput
                key={`p-${resetKey}`}
                parentRef={phoneRegisterRef}
                inputRef={phoneRef}
                placeholder="Phone"
                minLength={userPolicy.mobile.minLength}
                maxLength={userPolicy.mobile.maxLength}
                mask={/[0-9]/}
                format="phone"
                countries={countries}
                country={country}
                listClass="top-10"
                initialValue={`+${user?.country?.dialCode || country.value} ${user?.mobile ? user?.mobile?.replace(/(\d{3})(?=\d{3})/g, '$1 ').trim() : ''}`}
                tabIndex={(hasValue(user?.mobile) ? -1 : 0)}
                readOnly={(hasValue(user?.mobile))}
                error={errors.phone?.message}
                name={phoneRegister.name}
                showToast={_.toastError}
                onChange={onPhoneChange}
                onFocus={() => (lastFocusedRef.current = phoneRegister.name)}
                onBlur={onPhoneBlur}
              />
            </RowGrid>

            <RowGrid>
              <InputValidator
                ref={useMergeRefs(organizationRef, organizationRegisterRef)}
                placeholder="Organization"
                maxLength={userPolicy.organization.maxLength}
                mask={/[a-zA-Z0-9@. ]/}
                defaultValue={(hasValue(user?.organization) ? user?.organization : "")}
                tabIndex={(hasValue(user?.organization) ? -1 : 0)}
                readOnly={(hasValue(user?.organization))}
                value={watch("organization")}
                error={errors.organization?.message}
                {...organizationRegister}
                showToast={_.toastError}
                onFocus={() => (lastFocusedRef.current = organizationRegister.name)}
                onBlur={async (e) => {
                  await organizationRegisterOnBlur(e);
                  await trigger("organization");
                }}
              />

              <InputValidator
                ref={useMergeRefs(designationRef, designationRegisterRef)}
                placeholder="Designation"
                maxLength={userPolicy.designation.maxLength}
                mask={/[a-zA-Z0-9@ .()-]/}
                defaultValue={(hasValue(user?.designation) ? user?.designation : "")}
                tabIndex={(hasValue(user?.designation) ? -1 : 0)}
                readOnly={(hasValue(user?.designation))}
                value={watch("designation")}
                error={errors.designation?.message}
                {...designationRegister}
                showToast={_.toastError}
                onFocus={() => (lastFocusedRef.current = designationRegister.name)}
                onBlur={async (e) => {
                  await designationRegisterOnBlur(e);
                  await trigger("designation");
                }}
              />
            </RowGrid>

            <div className="border-t pt-4 dark:border-gray-800">
              <label className="block font-bold mb-2 text-black dark:text-ghost-white">
                {briefConfig.briefChoices.domains.text}
              </label>
              <div className="flex flex-col gap-2.5">
                {briefConfig.briefChoices.domains.options.map((opt: string, i: number) => (
                  <label key={i} className="flex items-start gap-3 cursor-pointer text-xs md:text-sm">
                    <input
                      type="checkbox"
                      value={opt}
                      className="accent-custom-blue-600 h-4 w-4 rounded"
                      {...register("selectedDomains", { required: "Please select at least one domain." })}
                    />
                    <span className="select-none text-custom-gray-700 dark:text-pale-white">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </FormProvider>
      </div>

      {/* DYNAMIC STEPS Form Layout Wrapper */}
      <FormProvider {...domainsForm}>
        {dynamicSteps.map((meta, idx) => {
          const stepNumber = idx + 1;
          if (meta.type !== "domain") return null;
          const domainKey = meta.key as BriefDomainKey;

          return (
            <div key={domainKey} style={{ display: step === stepNumber ? "block" : "none" }} className="flex flex-col px-4 gap-5">
              <div>
                <h3 className="text-base font-extrabold text-black dark:text-ghost-white mb-1">
                  {meta.config.heading}
                </h3>
                <p className="text-xs text-custom-gray-600 dark:text-pale-white italic">Please select an option for each question.</p>
              </div>

              {meta.config.questions.map((qObj: any, qIdx: number) => (
                <div key={qIdx} className="bg-white/5 p-4 rounded border border-gray-200/10 dark:bg-black/10">
                  <p className="text-xs md:text-sm font-semibold mb-3 text-custom-gray-700 dark:text-ghost-white">
                    {qObj.question}
                  </p>
                  <div className="flex flex-col gap-2">
                    {qObj.options.map((option: string, optIdx: number) => {
                      const fieldPath = `${domainKey}.questions.q_${qIdx}`;
                      return (
                        <label key={optIdx} className="flex items-start gap-3 cursor-pointer text-xs md:text-sm">
                          <input
                            type="radio"
                            value={option}
                            className="mt-0.5 accent-custom-blue-600"
                            {...domainsForm.register(fieldPath, {
                              required: true
                            })}
                          />
                          <span className="text-custom-gray-600 dark:text-pale-white">{option}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </FormProvider>

      {/* READINESS STEP Form Layout Wrapper */}
      <div style={{ display: currentMeta?.type === "readiness" ? "block" : "none" }}>
        <FormProvider {...readinessForm}>
          <div className="flex flex-col px-4 gap-2">
            {briefConfig.readiness && (
              <>
                <div>
                  <h3 className="text-base font-extrabold text-black dark:text-ghost-white">
                    {briefConfig.readiness.question}
                  </h3>
                </div>

                <div className="flex flex-col">
                  {briefConfig.readiness.options.map((option: string, optIdx: number) => (
                    <label key={optIdx} className="flex items-center gap-3 cursor-pointer text-xs md:text-sm p-2 rounded hover:bg-gray-500/5">
                      <input
                        type="radio"
                        value={option}
                        className="accent-custom-blue-600 h-4 w-4"
                        {...readinessForm.register("readiness", {
                          required: true
                        })}
                      />
                      <span className="font-medium text-custom-gray-700 dark:text-ghost-white">{option}</span>
                    </label>
                  ))}
                </div>

                <div>
                  <p className="max-w-104 text-xs md:text-sm text-custom-gray-600 dark:text-pale-white italic leading-relaxed bg-blue-500/5 p-3 rounded border border-blue-500/20">
                    {briefConfig.readiness.text}
                  </p>
                </div>
              </>
            )}
          </div>
        </FormProvider>
      </div>
    </div>
  );
  //endregion HTML Elements

  return (
    <div
      className="relative w-full h-full flex flex-col rounded-sm md:rounded-md overflow-hidden"
      onKeyDown={_onKeyDown}
      onWheel={(e) => e.stopPropagation()}>
      {loading && (
        loadingSpinner
      )}

      {toolbarTop}

      {content}

      {!isOverflow ? (
        staticFooter
      ) : (
        fixedFooter
      )}
    </div>
  );
}